import { useCallback, useEffect } from "react";

/** True when a history state object carries the calculator's back-button sentinel. */
function hasBfnSentinel(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { bfnCalculator?: unknown }).bfnCalculator === true
  );
}

/**
 * Back-button integration for the BFN Calculator overlay.
 *
 * Mobile/browser back button closes the overlay instead of navigating away
 * from the chat: push a sentinel history entry while the overlay is VISIBLE;
 * pressing back pops it, and the popstate handler closes the view.
 *
 * Two safety rules, both router-driven (the app runs BrowserRouter, which
 * keeps its POP-tracking `idx`/`usr`/`key` in history.state):
 * 1. The sentinel push MERGES into the current entry's state — clobbering
 *    it would break react-router's popstate delta calculation.
 * 2. The hide/unmount cleanup NEVER traverses. A history.back() here could
 *    race a navigation already in flight (logout's replace to /login) and
 *    pull the user away from where the router just put them. Instead it only
 *    strips the sentinel key in place; the leftover same-URL entry is
 *    harmless. The header X consumes the entry via history.back() in its
 *    own click handler (user-initiated, nothing else navigating), so the
 *    normal close paths still leave history exactly as it was.
 *
 * The popstate handler only closes when the popped-to entry is NOT the
 * sentinel, which keeps StrictMode's dev double-mount from self-closing.
 *
 * Keyed on `active` (visibility), not mount: with keep-alive the component
 * outlives a close, so the sentinel lifecycle must follow VISIBILITY — push
 * on show, strip-in-place on hide — which is byte-for-byte the old
 * mount/unmount semantics whenever keep-alive is off (a mounted view is then
 * always visible).
 *
 * Returns the header-X close handler: it consumes the sentinel entry so a
 * later back press doesn't need two presses — the resulting popstate
 * completes the close. Falls back to a direct close if the sentinel already
 * left the top of the stack.
 */
export function useBfnHistorySentinel(
  active: boolean,
  close: () => void,
): () => void {
  useEffect(() => {
    if (!active) {
      return;
    }
    window.history.pushState(
      { ...window.history.state, bfnCalculator: true },
      "",
    );
    const handlePopState = (event: PopStateEvent) => {
      if (!hasBfnSentinel(event.state)) {
        close();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (hasBfnSentinel(window.history.state)) {
        const rest = { ...(window.history.state as Record<string, unknown>) };
        delete rest.bfnCalculator;
        window.history.replaceState(rest, "");
      }
    };
  }, [active, close]);

  return useCallback(() => {
    if (hasBfnSentinel(window.history.state)) {
      window.history.back();
    } else {
      close();
    }
  }, [close]);
}
